import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "May 11, 2026";

export const Route = createFileRoute("/dmca")({
  head: () => ({
    meta: [
      { title: "DMCA / Copyright Policy | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "Copyright takedown policy and designated agent for Colorado Web Impressions under the Digital Millennium Copyright Act.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "DMCA / Copyright Policy | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/dmca` }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "DMCA", path: "/dmca" },
      ]),
    ],
  }),
  component: DmcaPage,
});

function DmcaPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Legal"
        title="DMCA / Copyright Policy"
        description={`Last updated: ${LAST_UPDATED}. How to report copyright infringement or file a counter-notice.`}
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <h2>Our position</h2>
            <p>
              Colorado Web Impressions respects the intellectual property of
              others and expects users to do the same. In accordance with the
              Digital Millennium Copyright Act (&ldquo;DMCA&rdquo;), we will
              respond promptly to valid notices of copyright infringement
              regarding material posted on coloradowebimpressions.com.
            </p>

            <h2>Filing a takedown notice</h2>
            <p>
              If you believe content on this site infringes your copyright,
              send a written notice to our designated agent (below) that
              includes:
            </p>
            <ol>
              <li>
                Your physical or electronic signature (or that of the rights
                holder you represent).
              </li>
              <li>
                Identification of the copyrighted work you claim has been
                infringed.
              </li>
              <li>
                The specific URL(s) on coloradowebimpressions.com where the
                allegedly infringing material is located.
              </li>
              <li>Your name, address, phone number, and email.</li>
              <li>
                A statement that you have a good-faith belief the use is not
                authorized by the copyright owner, its agent, or the law.
              </li>
              <li>
                A statement, under penalty of perjury, that the information in
                your notice is accurate and that you are authorized to act on
                behalf of the rights holder.
              </li>
            </ol>

            <h2>Designated agent</h2>
            <p>
              Chris Heidlebaugh, Colorado Web Impressions
              <br />
              Colorado Springs, Colorado, USA
              <br />
              Email:{" "}
              <a
                href="mailto:dmca@coloradowebimpressions.com"
                className="font-bold text-brand-gold-deep underline"
               aria-label="Email Colorado Web Impressions">
                dmca@coloradowebimpressions.com
              </a>
              <br />
              Phone:{" "}
              <a
                href="tel:+17194193935"
                className="font-bold text-brand-gold-deep underline"
               aria-label="Call (719) 419-3935">
                (719) 419-3935
              </a>
            </p>

            <h2>Counter-notice</h2>
            <p>
              If your content was removed in response to a DMCA notice and you
              believe the removal was a mistake or misidentification, you may
              submit a counter-notice that includes your signature, the
              material removed and its prior location, a statement under
              penalty of perjury that you have a good-faith belief the removal
              was in error, your name, address, phone number, and consent to
              the jurisdiction of the federal court in Colorado.
            </p>

            <h2>Misrepresentation</h2>
            <p>
              Under 17 U.S.C. § 512(f), any person who knowingly materially
              misrepresents that material is infringing — or that it was
              removed by mistake — may be liable for damages.
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
